import { useEffect, useState } from "react";
import { application } from "../../../_runtime/bootstrap";

/**
 * Functional Component: DynamicNameBreadcrumb
 *
 * This component dynamically sets the breadcrumb text based on data fetched from an API.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.match - The match object from react-router.
 * @param {string} props.request - The base URL for the API request.
 * @param {string} props.paramsProp - The property name in the match params to be used in the API request.
 * @param {string} [props.nameProp="name"] - The property name in the API response to be used for the breadcrumb text.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the breadcrumb text.
 */
export const DynamicNameBreadcrumb = ({
  match,
  request,
  paramsProp,
  nameProp = "name",
}: any) => {
  useEffect(() => {
    application
      .getHttpClient()
      .get(`${request}/${match.params[paramsProp]}`)
      .then((res: any) => {
        if (document.getElementById(`text-${match.pathname}`)) {
          document.getElementById(`text-${match.pathname}`)!.innerHTML =
            res.body[nameProp];
        }
      });
  }, []);
  return <span id={`text-${match.pathname}`}></span>;
};

/**
 * Array: modifiedRoutes
 *
 * This array contains route configuration objects for setting up breadcrumbs in the application.
 * Each object includes a path and a breadcrumb configuration which can be a static string or a dynamic component.
 *
 * @property {string} path - The path of the route.
 * @property {string | Function} breadcrumb - The breadcrumb configuration, either a static string or a dynamic component.
 */
export const modifiedRoutes = [
  { path: "/lecturers", breadcrumb: "Home" },
  { path: "/students", breadcrumb: "Home" },
  { path: "/management/users", breadcrumb: "Users" },
  { path: "/management", breadcrumb: "Home" },
  { path: "/testing", breadcrumb: "Home" },
  { path: "/users", breadcrumb: "Home" },
  { path: "/components-db", breadcrumb: "Components Database" },
  {
    path: "/courses",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":id",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/coursesms/courses"
            paramsProp="id"
          />
        ),
      },
    ],
  },
  {
    path: "/courses/:id/assignments",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":assignmentId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/coursesms/assignments"
            paramsProp="assignmentId"
            nameProp="title"
          />
        ),
      },
    ],
  },
  {
    path: "/courses/:id/assignments/:assignmentId/specification",
    children: [
      {
        path: ":userId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/usersms/users"
            paramsProp="userId"
            nameProp="displayName"
          />
        ),
        children: [
          {
            path: ":buildingConfigurationId",
            breadcrumb: ({ match }: any) => (
              <DynamicNameBreadcrumb
                match={match}
                request="/buildingsconfigms/configurations"
                paramsProp="buildingConfigurationId"
                nameProp="name"
              />
            ),
          }
        ]
      },
    ],
  },
  {
    path: "/courses/:id/groups",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":groupId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/coursesms/groups"
            paramsProp="groupId"
            nameProp="name"
          />
        ),
      },
    ],
  },
  {
    path: "/assignments/:courseId",
    breadcrumb: ({ match }: any) => (
      <DynamicNameBreadcrumb
        match={match}
        request="/coursesms/courses"
        paramsProp="courseId"
      />
    ),
  },
  {
    path: "/assignments/:courseId/:assignmentId",
    breadcrumb: ({ match }: any) => (
      <DynamicNameBreadcrumb
        match={match}
        request="/coursesms/assignments"
        paramsProp="assignmentId"
        nameProp="title"
      />
    ),
    children: [
      {
        path: "specification/:buildingConfigurationId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingsconfigms/configurations"
            paramsProp="buildingConfigurationId"
            nameProp="name"
          />
        ),
      }
    ]
  },
  {
    path: "/components-db/data/materials",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":materialId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingsms/materials"
            paramsProp="materialId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/data/building-parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":buildingParameterId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/categories/parameters"
            paramsProp="buildingParameterId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/data/space-functions/spaces",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":spaceFunctionId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/space-functions"
            paramsProp="spaceFunctionId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/data/space-functions/parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":parameterId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/categories/parameters"
            paramsProp="parameterId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/floors/types",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":floorTypeId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingsms/types/elements"
            paramsProp="floorTypeId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/floors/parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":parameterId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/categories/parameters"
            paramsProp="parameterId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/walls/types",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":wallTypeId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingsms/types/elements"
            paramsProp="wallTypeId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/walls/parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":parameterId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/categories/parameters"
            paramsProp="parameterId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/windows/types",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":windowTypeId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingsms/types/elements"
            paramsProp="windowTypeId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/windows/parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":parameterId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/categories/parameters"
            paramsProp="parameterId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/roofs/types",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":roofTypeId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingsms/types/elements"
            paramsProp="roofTypeId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/components/roofs/parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":parameterId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/categories/parameters"
            paramsProp="parameterId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/presets/space-functions",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":presetId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/presets"
            paramsProp="presetId"
          />
        ),
      },
    ],
  },
  {
    path: "/components-db/presets/building-parameters",
    children: [
      {
        path: "new",
        breadcrumb: "New",
      },
      {
        path: ":presetId",
        breadcrumb: ({ match }: any) => (
          <DynamicNameBreadcrumb
            match={match}
            request="/buildingms/presets"
            paramsProp="presetId"
          />
        ),
      },
    ],
  },
  {
    path: "/building-configurations/review/:buildingConfigId",
    breadcrumb: ({ match }: any) => (
      <DynamicNameBreadcrumb
        match={match}
        request="/buildingsconfigms/configurations"
        paramsProp="buildingConfigId"
        nameProp="name"
      />
    ),
  },
];
